import type { Signal } from "@builder.io/qwik";
import { component$, useSignal, useStyles$, $, useStore, useVisibleTask$, useOnDocument } from "@builder.io/qwik";
import { FancyImage } from "../../common-components/fancy-image/FancyImage";
import { getInspectProps } from "../../utils/getInspectProps";
import { cn } from "../../utils/tailwind-helper";
import type { SliderWideProps } from "./SliderWide";
import styles from "./slider-wide-desktop.css?inline";
import type { IGenAsset, Maybe } from "../../services/graphql/__generated/sdk";
import { SliderIndexDisplay } from "./SliderIndexDisplay";
import { BREAKPOINTS } from "../../constants/mediaquery";
import { AutoplayButton } from "../../common-components/autoplay-button/AutoplayButton";

/**
 * Constants for slider positioning and animation
 */
export const POSITIONS = {
    MAIN_BACKGROUND: 0, // Main display position - large center image
    MAIN_FOREGROUND: 1, // Secondary large image
    LEFT_THUMBNAIL: 2, // Left thumbnail
    CENTER_THUMBNAIL: 3, // Center thumbnail
    RIGHT_THUMBNAIL: 4, // Right thumbnail (faded)
    RESET_POSITION: 5, // Position that moves to the far left side as a wait position
};

export const NAVIGATION_TYPE = {
    NONE: "none",
    GO_ONE: "go_one",
    GO_TWO: "go_two",
};

const DEFAULT_POSITION_CLASSES = {
    [POSITIONS.RESET_POSITION]: "z-[1] translate-x-[136px] opacity-0",
    [POSITIONS.MAIN_BACKGROUND]: "z-[12] translate-x-[190px] translate-y-[50px] scale-x-[1050%] scale-y-[1050%]",
    [POSITIONS.MAIN_FOREGROUND]: "z-[13] translate-x-[190px] translate-y-[50px] scale-x-[1050%] scale-y-[1050%]",
    [POSITIONS.LEFT_THUMBNAIL]: "z-[14] translate-x-[-136px] hover:scale-[120%] hover:scale-y-[120%]",
    [POSITIONS.CENTER_THUMBNAIL]: "z-[14] hover:scale-[120%] hover:scale-y-[120%]",
    [POSITIONS.RIGHT_THUMBNAIL]: "z-[14] translate-x-[136px] opacity-0 hover:scale-[120%] hover:scale-y-[120%]",
};
const POSITION_CLASSES = {
    [NAVIGATION_TYPE.NONE]: DEFAULT_POSITION_CLASSES,
    [NAVIGATION_TYPE.GO_ONE]: {
        [POSITIONS.RESET_POSITION]: "z-[1] translate-x-[136px] opacity-0",
        [POSITIONS.MAIN_BACKGROUND]: "z-[12] translate-x-[190px] translate-y-[50px] scale-x-[1050%] scale-y-[1050%]",
        [POSITIONS.MAIN_FOREGROUND]: "z-[13] translate-x-[190px] translate-y-[50px] scale-x-[1050%] scale-y-[1050%]",
        [POSITIONS.LEFT_THUMBNAIL]: "z-[14] translate-x-[-136px] hover:scale-[120%] hover:scale-y-[120%]",
        [POSITIONS.CENTER_THUMBNAIL]: "z-[15] hover:scale-[120%] hover:scale-y-[120%]",
        [POSITIONS.RIGHT_THUMBNAIL]: "z-[15] translate-x-[136px] opacity-0 hover:scale-[120%] hover:scale-y-[120%]",
    },
    [NAVIGATION_TYPE.GO_TWO]: {
        [POSITIONS.RESET_POSITION]: "z-[1] translate-x-[136px] opacity-0",
        [POSITIONS.MAIN_BACKGROUND]: "z-[12] translate-x-[190px] translate-y-[50px] scale-x-[1050%] scale-y-[1050%]",
        [POSITIONS.MAIN_FOREGROUND]: "z-[13] translate-x-[190px] translate-y-[50px] scale-x-[1050%] scale-y-[1050%]",
        [POSITIONS.LEFT_THUMBNAIL]: "z-[14] translate-x-[-136px] hover:scale-[120%] hover:scale-y-[120%]",
        [POSITIONS.CENTER_THUMBNAIL]: "z-[15] hover:scale-[120%] hover:scale-y-[120%]",
        [POSITIONS.RIGHT_THUMBNAIL]: "z-[15] translate-x-[136px] opacity-0 hover:scale-[120%] hover:scale-y-[120%]",
    },
};

export const ANIMATION_DURATION = {
    NORMAL: 50,
    WRAP_AROUND: 100,
    IN_ANIMATION: 300,
    COMPLETE: 350,
};

/**
 * Type definitions for better code organization
 */

export interface Slide {
    image: (IGenAsset & { forceInvisible?: boolean }) | null;
    displayPosition: number;
}

export interface SlidesState {
    slides: Slide[];
}

/**
 * CardSlidePosition Component
 * Renders a single slide at a specific position in the carousel
 */
export const CardSlidePosition = component$(
    ({
        img,
        displayPosition,
        componentIndex,
        onClick$,
        navigationType,
        componentWidth,
    }: {
        img: (IGenAsset & { forceInvisible?: boolean }) | null;
        displayPosition: number;
        componentIndex: number;
        componentWidth: number;
        onClick$?: () => void;
        navigationType: Signal<string>;
    }) => {
        // The clickable check depends on having a click handler and being in a thumbnail position
        const isClickable =
            onClick$ !== undefined &&
            (displayPosition === POSITIONS.LEFT_THUMBNAIL || displayPosition === POSITIONS.CENTER_THUMBNAIL);

        // const thumb
        const safeComponentWidth = componentWidth > 0 ? componentWidth : BREAKPOINTS.SILVER;
        const enlargePercentFloat = (safeComponentWidth / 120) * 100;
        const enlargePercent = (enlargePercentFloat + 1).toFixed(0);
        // 120px == 100% width
        // componentWidth == x% width

        const prevComponentWidth = useSignal<number>(0);
        const componentWithChanged = useSignal(false);
        const componentWidthChangedTimeoutRef = useSignal<any>(null);

        // Track componentWidth changes and set componentWithChanged flag - THIS IS JUST SO ON RESIZE THE ANIMATION DOES NOT HAPPEN FOR LESS FLICKER
        // eslint-disable-next-line qwik/no-use-visible-task
        useVisibleTask$(({ track, cleanup }) => {
            // Track changes to componentWidth
            const currentWidth = track(() => componentWidth);

            // Check if width has changed from the previous value
            if (currentWidth !== prevComponentWidth.value && currentWidth > 0) {
                // Width changed, update the state
                prevComponentWidth.value = currentWidth;
                componentWithChanged.value = true;

                // Clear any existing timeout to prevent early reset
                if (componentWidthChangedTimeoutRef.value) {
                    clearTimeout(componentWidthChangedTimeoutRef.value);
                }

                // Set new timeout to reset the flag after 100ms
                componentWidthChangedTimeoutRef.value = setTimeout(() => {
                    componentWithChanged.value = false;
                    componentWidthChangedTimeoutRef.value = null;
                }, 100);
            }

            // Clean up timeout on component unmount
            cleanup(() => {
                if (componentWidthChangedTimeoutRef.value) {
                    clearTimeout(componentWidthChangedTimeoutRef.value);
                }
            });
        });

        // Custom style for easing curve - can't be done with Tailwind classes
        const customStyle = {
            transition: `transform ${ANIMATION_DURATION.IN_ANIMATION}ms cubic-bezier(0.4, 0, 0.2, 1), opacity ${ANIMATION_DURATION.IN_ANIMATION}ms cubic-bezier(0.4, 0, 0.2, 1), scale ${ANIMATION_DURATION.IN_ANIMATION}ms cubic-bezier(0.4, 0, 0.2, 1), z-index 0s`,
            ...(displayPosition === POSITIONS.MAIN_BACKGROUND || displayPosition === POSITIONS.MAIN_FOREGROUND
                ? {
                      // transform: `translateX(190px) translateY(50px) scaleX(1050%) scaleY(1050%)`,
                      transform: `translateX(48px) translateY(50px) scaleX(${enlargePercent}%) scaleY(${enlargePercent}%)`,
                      // translate-x-[190px] translate-y-[50px] scale-x-[1050%] scale-y-[1050%]
                  }
                : {}),
            ...(componentWithChanged.value ? { transition: "none", transitionDuration: "0" } : {}),
        };

        // For wrap-around case, handle additional invisibility
        const forceInvisible = img?.forceInvisible === true;

        return (
            <div
                class={cn(
                    // Base styles
                    "h-[73px] w-[120px] cursor-pointer rounded !bg-transparent",
                    "group",
                    "absolute right-0 top-0",
                    "xslide",
                    "opacity-1",
                    "[transform-origin:bottom_right]",
                    `duration-[${componentWithChanged.value ? 0 : ANIMATION_DURATION.IN_ANIMATION}ms]`,
                    `xdisplaypos${displayPosition}`,
                    !isClickable && "pointer-events-none",
                    forceInvisible && "!opacity-0",
                    POSITION_CLASSES[navigationType.value][displayPosition] || "",
                    ``,
                )}
                style={customStyle}
                onClick$={isClickable ? onClick$ : undefined}
            >
                {img?.src && (
                    <FancyImage
                        img={img}
                        lazyload={componentIndex > 0}
                        responsiveAspectRatio={{
                            bronze: 762 / 343,
                            gold: 1192 / 492,
                            platinum: 1360 / 575,
                        }}
                        noAspectRatio
                        class={cn(
                            "slider-wide ximage",
                            "h-[73px] w-[120px] rounded transition-colors",
                            // "border border-white-opacity-50"
                        )}
                    />
                )}
            </div>
        );
    },
);

/**
 * Main slider component that handles the carousel behavior
 */
export const CardSliderDesktop = component$(
    ({
        id,
        componentIndex,
        images: xImages,
        screensize,
    }: SliderWideProps & { screensize: Signal<number>; images: Maybe<Maybe<IGenAsset>[]> | undefined }) => {
        const images = xImages?.filter((img) => img?.src) || [];
        // Setup constants and basic state
        const totalImages = images?.length || 2; // we do not show the slider at all if we just have 1 image
        const isNavigating = useSignal(false);
        const navigationType = useSignal(NAVIGATION_TYPE.NONE);

        // a bit more for small buffer in the math later
        const componentWidth =
            screensize.value > 1440
                ? 1380
                : screensize.value > BREAKPOINTS.GOLD
                  ? screensize.value - 80
                  : screensize.value - 64;
        useStyles$(styles);

        /**
         * Slide state management using useStore instead of individual signals
         * This creates a more cohesive data structure and improves readability
         */
        const getImageAtIndex = (index) => {
            // Use modulo to wrap around for smaller collections
            return images[index % totalImages] || null;
          };

        const slidesStore = useStore<SlidesState>(
            {
                slides: [
                    { image: getImageAtIndex(0), displayPosition: POSITIONS.MAIN_BACKGROUND },
                    { image: getImageAtIndex(0), displayPosition: POSITIONS.MAIN_FOREGROUND },
                    { image: getImageAtIndex(1), displayPosition: POSITIONS.LEFT_THUMBNAIL },
                    { image: getImageAtIndex(2), displayPosition: POSITIONS.CENTER_THUMBNAIL },
                    { image: getImageAtIndex(3), displayPosition: POSITIONS.RIGHT_THUMBNAIL },
                    { image: getImageAtIndex(3), displayPosition: POSITIONS.RESET_POSITION },
                ],
            },
            { deep: true },
        );

        // Get current index
        const currentActiveImageIndex =
            images.findIndex(
                (img) =>
                    img?.id ==
                    slidesStore.slides.find((s) => s.displayPosition === POSITIONS.MAIN_FOREGROUND)?.image?.id,
            ) + 1;


            /**
         * Navigation functions
         */
        const goOne = $(() => {
            if (isNavigating.value) return; // Prevent rapid clicks
            isNavigating.value = true;

            navigationType.value = NAVIGATION_TYPE.GO_ONE;

            const byDisplayPostion = {
                [POSITIONS.MAIN_BACKGROUND]: slidesStore.slides.find(
                    (s) => s.displayPosition === POSITIONS.MAIN_BACKGROUND,
                )!,
                [POSITIONS.MAIN_FOREGROUND]: slidesStore.slides.find(
                    (s) => s.displayPosition === POSITIONS.MAIN_FOREGROUND,
                )!,
                [POSITIONS.LEFT_THUMBNAIL]: slidesStore.slides.find(
                    (s) => s.displayPosition === POSITIONS.LEFT_THUMBNAIL,
                )!,
                [POSITIONS.CENTER_THUMBNAIL]: slidesStore.slides.find(
                    (s) => s.displayPosition === POSITIONS.CENTER_THUMBNAIL,
                )!,
                [POSITIONS.RIGHT_THUMBNAIL]: slidesStore.slides.find(
                    (s) => s.displayPosition === POSITIONS.RIGHT_THUMBNAIL,
                )!,
                [POSITIONS.RESET_POSITION]: slidesStore.slides.find(
                    (s) => s.displayPosition === POSITIONS.RESET_POSITION,
                )!,
            };
            // Move the images to the next position
            // left thumbnail to main background
            byDisplayPostion[POSITIONS.LEFT_THUMBNAIL].displayPosition = POSITIONS.MAIN_FOREGROUND;
            // center thumbnail to left thumbnail
            byDisplayPostion[POSITIONS.CENTER_THUMBNAIL].displayPosition = POSITIONS.LEFT_THUMBNAIL;
            // right thumbnail to center thumbnail
            byDisplayPostion[POSITIONS.RIGHT_THUMBNAIL].displayPosition = POSITIONS.CENTER_THUMBNAIL;
            // main foreground to main background
            byDisplayPostion[POSITIONS.MAIN_FOREGROUND].displayPosition = POSITIONS.MAIN_BACKGROUND;
            // main background to reset position
            byDisplayPostion[POSITIONS.MAIN_BACKGROUND].displayPosition = POSITIONS.RESET_POSITION;
            // reset position to right thumbnail
            byDisplayPostion[POSITIONS.RESET_POSITION].displayPosition = POSITIONS.RIGHT_THUMBNAIL;

            // Update the images after a delay
            // we need to fill up the next upcoming image on RIGHT_THUMBNAIL and RESET_POSITION for the next step
            const currentImageIndex = images.findIndex(
                (ximg) => ximg?.id === byDisplayPostion[POSITIONS.RESET_POSITION].image?.id,
            );

            const nextImageIndex = (currentImageIndex + 1) % totalImages;
            const nextImage = images[nextImageIndex];
            const safedNextImage = nextImage ? { ...nextImage } : null;

            setTimeout(() => {
                slidesStore.slides.find((s) => s.displayPosition === POSITIONS.RIGHT_THUMBNAIL)!.image = safedNextImage;
                slidesStore.slides.find((s) => s.displayPosition === POSITIONS.RESET_POSITION)!.image = safedNextImage;

                isNavigating.value = false;
                navigationType.value = NAVIGATION_TYPE.NONE;
            }, ANIMATION_DURATION.COMPLETE);
        });

        const goTwo = $(() => {
            if (isNavigating.value) return; // Prevent rapid clicks
            isNavigating.value = true;

            navigationType.value = NAVIGATION_TYPE.GO_TWO;

            const byDisplayPostion = {
                [POSITIONS.MAIN_BACKGROUND]: slidesStore.slides.find(
                    (s) => s.displayPosition === POSITIONS.MAIN_BACKGROUND,
                )!,
                [POSITIONS.MAIN_FOREGROUND]: slidesStore.slides.find(
                    (s) => s.displayPosition === POSITIONS.MAIN_FOREGROUND,
                )!,
                [POSITIONS.LEFT_THUMBNAIL]: slidesStore.slides.find(
                    (s) => s.displayPosition === POSITIONS.LEFT_THUMBNAIL,
                )!,
                [POSITIONS.CENTER_THUMBNAIL]: slidesStore.slides.find(
                    (s) => s.displayPosition === POSITIONS.CENTER_THUMBNAIL,
                )!,
                [POSITIONS.RIGHT_THUMBNAIL]: slidesStore.slides.find(
                    (s) => s.displayPosition === POSITIONS.RIGHT_THUMBNAIL,
                )!,
                [POSITIONS.RESET_POSITION]: slidesStore.slides.find(
                    (s) => s.displayPosition === POSITIONS.RESET_POSITION,
                )!,
            };
            // Move the images to the next position
            // left thumbnail stays where it is
            // byDisplayPostion[POSITIONS.LEFT_THUMBNAIL].displayPosition = POSITIONS.LEFT_THUMBNAIL;
            // center thumbnail to main foreground
            byDisplayPostion[POSITIONS.CENTER_THUMBNAIL].displayPosition = POSITIONS.MAIN_FOREGROUND;
            // right thumbnail to center thumbnail
            byDisplayPostion[POSITIONS.RIGHT_THUMBNAIL].displayPosition = POSITIONS.CENTER_THUMBNAIL;
            // main foreground to main background
            byDisplayPostion[POSITIONS.MAIN_FOREGROUND].displayPosition = POSITIONS.MAIN_BACKGROUND;
            // main background to reset position
            byDisplayPostion[POSITIONS.MAIN_BACKGROUND].displayPosition = POSITIONS.RESET_POSITION;
            // reset position to right thumbnail
            byDisplayPostion[POSITIONS.RESET_POSITION].displayPosition = POSITIONS.RIGHT_THUMBNAIL;

            // Reset navigating flag after animation completes

            // we need to fill up the next upcoming image on RIGHT_THUMBNAIL and RESET_POSITION for the next step
            const currentImageIndex = images.findIndex(
                (ximg) => ximg?.id === byDisplayPostion[POSITIONS.RESET_POSITION].image?.id,
            );

            const nextImageIndex = (currentImageIndex + 1) % totalImages;
            const nextImage = images[nextImageIndex];
            const safedNextImage = nextImage ? { ...nextImage } : null;

            setTimeout(() => {
                slidesStore.slides.find((s) => s.displayPosition === POSITIONS.RIGHT_THUMBNAIL)!.image = safedNextImage;
                slidesStore.slides.find((s) => s.displayPosition === POSITIONS.RESET_POSITION)!.image = safedNextImage;

                isNavigating.value = false;
                navigationType.value = NAVIGATION_TYPE.NONE;
            }, ANIMATION_DURATION.COMPLETE);
        });

        const AUTOPLAY_SPEED = 8000;
        const isAutoPlaying = useSignal(false);
        const isControlledAutoplaying = useSignal(false);
        // turn auto play on dom-load
        useOnDocument(
            "DOMContentLoaded",
            $(() => {
                if (images && images.length > 1) {
                    isAutoPlaying.value = true;
                }
            }),
        );

        // eslint-disable-next-line qwik/no-use-visible-task
        useVisibleTask$(({ track, cleanup }) => {
            // THIS CODE IS NEEDED TO STAY OTHERWISE THE RERENDER WILL NOT WORK PROPERLY
            // This triggers a re-render when navigationType.value = xxx is called, and that is exactly what we want
            // that is why useVisibleTask and the the track function below needs to stay
            track(() => navigationType);
            // we also need this to cause rerenders
            track(isAutoPlaying);

            if (!isControlledAutoplaying.value) {
                console.log(` autoplay`, isAutoPlaying.value);
                isControlledAutoplaying.value = true;
                isAutoPlaying.value = true;
            }
 

            let lastTime = 0;
            let animationFrameId: number;

            const animate = (time: number) => {
                if (!isAutoPlaying.value) return;
                if (!lastTime) lastTime = time;
                const deltaTime = time - lastTime;

                if (deltaTime >= AUTOPLAY_SPEED) {
                    lastTime = time;
                  // run here
                  goOne();
                }

                animationFrameId = requestAnimationFrame(animate);
            };

            animationFrameId = requestAnimationFrame(animate);

            cleanup(() => {
                cancelAnimationFrame(animationFrameId);
            });
            // console.log(
            //     ` slides`,
            //     JSON.stringify(
            //         slidesStore.slides
            //             .map((s) => ({ displayPosition: s.displayPosition, title: s.image?.title }))
            //             .sort((a, b) => a.displayPosition - b.displayPosition),
            //         null,
            //         2,
            //     ),
            // );
        });

       

        console.log(` isAutoPlaying.value`, isAutoPlaying.value);

        return (
            <div class={cn("col-span-full", "desktop block w-full bronze:hidden", "silver:block")}>
                {/* <div class={cn("flex w-full flex-row gap-2.5")}>
                    <button onClick$={goOne} disabled={isNavigating.value}>
                        NEXT
                    </button>
                    <div>
                        Active Slide: {currentActiveImageIndex} / {totalImages}
                    </div>
                </div> */}

                {images && images.length > 0 && (
                    <div
                        class={cn(
                            "relative flex h-full w-full flex-col gap-2.5",
                            "silver:aspect-[762/343]",
                            "gold:aspect-[1192/492]",
                            "platinum:aspect-[1360/575]",
                            "overflow-hidden rounded-lg",
                        )}
                        {...getInspectProps({ id, fieldName: "images" })}
                    >
                        <SliderIndexDisplay totalCount={totalImages} currentActiveImageIndex={currentActiveImageIndex}>
                            <AutoplayButton
                                isAutoPlaying={isAutoPlaying}
                                activeSlideIndex={{ value: currentActiveImageIndex }}
                                transitionState={{ value: "" }}
                                AUTOPLAY_SPEED={AUTOPLAY_SPEED}
                                onToggleAutoplay$={() => (isAutoPlaying.value = !isAutoPlaying.value)}
                                buttonClassName="w-[29px] h-[29px]"
                                innerSVGClassName={isAutoPlaying.value ? "w-[8px] h-[8px]" : "w-[12px] h-[12px]"}
                            />
                        </SliderIndexDisplay>
                        <div class={cn("z-30 silver:absolute silver:bottom-8 silver:right-8")}>
                            <div class={cn("xslidecontainer h-[73px] w-[120px]")}>
                                {/* Render all slide positions */}
                                {slidesStore.slides.map((slide, physicalIndex) => {
                                    // Find which thumbnails are clickable
                                    const isLeftThumb = slide.displayPosition === POSITIONS.LEFT_THUMBNAIL;
                                    const isCenterThumb = slide.displayPosition === POSITIONS.CENTER_THUMBNAIL;

                                    return (
                                        <CardSlidePosition
                                            componentWidth={componentWidth}
                                            key={`position-${physicalIndex}`}
                                            displayPosition={slide.displayPosition}
                                            navigationType={navigationType}
                                            img={slide.image}
                                            componentIndex={componentIndex}
                                            onClick$={isLeftThumb ? goOne : isCenterThumb ? goTwo : undefined}
                                        />
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    },
);
